/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/App.tsx
 * Release: v2.0.0 - App principale Coronatek V2 con autenticazione completa
 * Data: Dicembre 2024
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useEffect } from 'react'
import { AuthProvider } from './components/auth/AuthProvider'
import { ToastProvider } from './components/ui/ToastProvider'
import AppDialogHost from './components/AppDialogHost'
import PieroZoomer from './components/PieroZoomer'
import BancaDatiNotifier from './components/banca-dati/BancaDatiNotifier'
import { OrphanPhotoWatcher } from './components/remote-photo/OrphanPhotoWatcher'
import { TenantProvider } from './core/contexts/TenantContext'
import { PendingCountsProvider } from './contexts/PendingCountsContext'
import { SedeProvider } from './contexts/SedeContext'
import { DevSimulatorProvider, DevSimulatorButton, DevSimulatorPanel, DevSimulatorConfirmModal } from './components/dev'
import GlobalModalsProvider from './components/admin/GlobalModalsProvider'
import { OperatoreAttivoProvider } from './contexts/OperatoreAttivoContext'
import RemoteScanProvider from './core/components/RemoteScan/RemoteScanProvider'
import MobileScanReceiver from './core/components/RemoteScan/MobileScanReceiver'
import { DesktopChatWidget } from './components/chat'
import StickyOperatorLoginButton from './components/mobile-operators/StickyOperatorLoginButton'
import CustomSignIn from './components/auth/CustomSignIn'
import CustomSignUp from './components/auth/CustomSignUp'
import DirectLogin from './components/auth/DirectLogin'
// lazy: ProfileRoutePage  (was: import ProfileRoutePage from './pages/ProfileRoutePage')
import RequireAuth from './components/auth/RequireAuth'
import AppLayout from './components/templates/AppLayout'
import AdminPageLayout from './components/templates/AdminPageLayout'
// lazy: OfficinaPage  (was: import OfficinaPage from './pages/OfficinaPage')
// lazy: MagazzinoPage  (was: import MagazzinoPage from './pages/MagazzinoPage')
// lazy: MagazzinoV2Page  (was: import MagazzinoV2Page from './pages/MagazzinoV2Page')
// lazy: PagamentiPage  (was: import PagamentiPage from './pages/PagamentiPage')
// lazy: PagamentoPage  (was: import PagamentoPage from './pages/PagamentoPage')
// lazy: AccettazionePage  (was: import AccettazionePage from './pages/AccettazionePage')
// lazy: DigitalizzaBigliettoPage  (was: import DigitalizzaBigliettoPage from './pages/DigitalizzaBigliettoPage')
// lazy: CompilaDatiEssenzialiPage  (was: import CompilaDatiEssenzialiPage from './pages/CompilaDatiEssenzialiPage')
// lazy: TestAccettazioneMenuPage  (was: import TestAccettazioneMenuPage from './pages/TestAccettazioneMenuPage')
// lazy: AnalisiGuastoPage  (was: import AnalisiGuastoPage from './pages/AnalisiGuastoPage')
// lazy: AnalisiGuastiPage  (was: import AnalisiGuastiPage from './pages/AnalisiGuastiPage')
// lazy: AnalisiGuastiPageOld  (was: import AnalisiGuastiPageOld from './pages/AnalisiGuastiPageOld')
// lazy: UfficioPage  (was: import UfficioPage from './pages/UfficioPage')
// lazy: ClientiPage  (was: import ClientiPage from './pages/ClientiPage')

import VerifyEmailPage from './components/auth/VerifyEmailPage'

const ProfileRoutePage = lazy(() => import('./pages/ProfileRoutePage'))
const OfficinaPage = lazy(() => import('./pages/OfficinaPage'))
const MagazzinoPage = lazy(() => import('./pages/MagazzinoPage'))
const AuditOperatoriPage = lazy(() => import('./pages/admin/AuditOperatoriPage'))
const MagazzinoV2Page = lazy(() => import('./pages/MagazzinoV2Page'))
const PagamentiPage = lazy(() => import('./pages/PagamentiPage'))
const PagamentoPage = lazy(() => import('./pages/PagamentoPage'))
const AccettazionePage = lazy(() => import('./pages/AccettazionePage'))
const DigitalizzaBigliettoPage = lazy(() => import('./pages/DigitalizzaBigliettoPage'))
const CompilaDatiEssenzialiPage = lazy(() => import('./pages/CompilaDatiEssenzialiPage'))
const TestAccettazioneMenuPage = lazy(() => import('./pages/TestAccettazioneMenuPage'))
const AnalisiGuastoPage = lazy(() => import('./pages/AnalisiGuastoPage'))
const AnalisiGuastiPage = lazy(() => import('./pages/AnalisiGuastiPage'))
const AnalisiGuastiPageOld = lazy(() => import('./pages/AnalisiGuastiPageOld'))
// CoronaTek Preventivi — webapp mobile approvatori (Fase 1, 2026-07-16)
const MobilePreventiviLayout = lazy(() => import('./pages/mobile-preventivi/MobilePreventiviLayout'))
const MobilePreventiviCoda = lazy(() => import('./pages/mobile-preventivi/CodaAnalisiPage'))
const MobilePreventiviRevisione = lazy(() => import('./pages/mobile-preventivi/RevisioneAnalisiPage'))
const MobilePreventiviPreventivo = lazy(() => import('./pages/mobile-preventivi/PreventivoMobilePage'))
const MobilePreventiviValorizzazioni = lazy(() => import('./pages/mobile-preventivi/ValorizzazioniPage'))
const MobilePreventiviStampe = lazy(() => import('./pages/mobile-preventivi/StampePage'))
const MobilePreventiviDaApprovare = lazy(() => import('./pages/mobile-preventivi/DaApprovarePage'))
const MobilePreventiviClienti = lazy(() => import('./pages/mobile-preventivi/ClientiDaApprovarePage'))
const MobilePreventiviApparato = lazy(() => import('./pages/mobile-preventivi/ApparatoFineLavoroPage'))
const UfficioPage = lazy(() => import('./pages/UfficioPage'))
const ClientiPage = lazy(() => import('./pages/ClientiPage'))
const TestAdminPage = lazy(() => import('./pages/TestAdminPage'))
const TestControlliPage = lazy(() => import('./pages/TestControlliPage'))
const TestMockupPreventivoPage = lazy(() => import('./pages/TestMockupPreventivoPage'))
const TestProfessionalPage = lazy(() => import('./components/professional/TestPage'))
const TestModalScrollPage = lazy(() => import('./pages/TestModalScrollPage'))
const TestLandscapeWidthPage = lazy(() => import('./pages/TestLandscapeWidthPage'))
const UIDialogicomPage = lazy(() => import('./pages/UIDialogicomPage'))
const UIDialogicomRel2Page = lazy(() => import('./pages/UIDialogicomRel2Page'))
const TestUniversalPhotoCropPage = lazy(() => import('./pages/TestUniversalPhotoCrop'))
const TestUniversalPhotoCropSimplePage = lazy(() => import('./pages/TestUniversalPhotoCropSimple'))
const MobileCameraPage = lazy(() => import('./pages/MobileCameraPage'))
const MobileCameraPersistentPage = lazy(() => import('./pages/MobileCameraPersistentPage'))
const MobilePhotoRequestsPage = lazy(() => import('./pages/MobilePhotoRequestsPage'))
const MobileRemotePhoto = lazy(() => import('./pages/MobileRemotePhoto'))
const MobileRemoteScan = lazy(() => import('./pages/MobileRemoteScan'))
const PushTestPage = lazy(() => import('./pages/PushTestPage'))
const TestRemoteCameraPage = lazy(() => import('./pages/TestRemoteCameraPage'))
const BusinessCardOCRPage = lazy(() => import('./pages/BusinessCardOCRPage'))
const UserListPage = lazy(() => import('./pages/admin/UserListPage'))
const GroupListPage = lazy(() => import('./pages/admin/GroupListPage'))
const UserProfileFormPage = lazy(() => import('./pages/admin/UserProfileFormPage'))
const PendingCustomersPage = lazy(() => import('./pages/admin/PendingCustomersPage'))
const PendingTurbinesPage = lazy(() => import('./pages/admin/PendingTurbinesPage'))
const PendingPage = lazy(() => import('./pages/admin/PendingPage'))
const NuovoInserimentoDesktopPage = lazy(() => import('./pages/admin/NuovoInserimentoDesktopPage'))
const DesktopBusinessCardPage = lazy(() => import('./pages/admin/DesktopBusinessCardPage'))
const AccettazioneRapidaPage = lazy(() => import('./pages/admin/AccettazioneRapidaPage'))
const InserimentoClienteRapidoPage = lazy(() => import('./pages/admin/InserimentoClienteRapidoPage'))
const OpenAIConfigPage = lazy(() => import('./pages/admin/OpenAIConfigPage'))
const AIKnowledgeBasePage = lazy(() => import('./pages/admin/AIKnowledgeBasePage'))
const ConfigurationHubPageStandalone = lazy(() => import('./pages/admin/ConfigurationHubPageStandalone'))
const MobileOperatorsManagementPage = lazy(() => import('./pages/admin/MobileOperatorsManagementPage'))
const AccettazioneV2Page = lazy(() => import('./pages/admin/AccettazioneV2Page'))
const DesktopPhotoRequestsPage = lazy(() => import('./pages/DesktopPhotoRequestsPage'))
const DataCleanupPage = lazy(() => import('./pages/admin/DataCleanupPage'))
const MagazzinoSyncPage = lazy(() => import('./pages/admin/MagazzinoSyncPage'))
const ForceUpdatePage = lazy(() => import('./pages/admin/ForceUpdatePage'))
const MobileOperatorWelcomePage = lazy(() => import('./pages/MobileOperatorWelcomePage'))
const MobileOperatorQRLoginPage = lazy(() => import('./pages/MobileOperatorQRLoginPage'))
const MobileOperatorDashboardPage = lazy(() => import('./pages/MobileOperatorDashboardPage'))
const MobileOperatorChangePINPage = lazy(() => import('./pages/MobileOperatorChangePINPage'))
const AccettazionePageMobile = lazy(() => import('./pages/mobile-operator/AccettazionePage'))
const AnalisiPageMobile = lazy(() => import('./pages/mobile-operator/AnalisiPage'))
const RiparazionePageMobile = lazy(() => import('./pages/mobile-operator/RiparazionePage'))
const ConsegnaPageMobile = lazy(() => import('./pages/mobile-operator/ConsegnaPage'))
const UtilityPageMobile = lazy(() => import('./pages/mobile-operator/UtilityPage'))
const PhotoCapturePage = lazy(() => import('./pages/mobile-operator/PhotoCapturePage'))
const WorkOrdersPage = lazy(() => import('./pages/mobile-operator/WorkOrdersPage'))
const CheckInsPage = lazy(() => import('./pages/mobile-operator/CheckInsPage'))
const InventoryPage = lazy(() => import('./pages/mobile-operator/InventoryPage'))
// --- Mockup modulo Apparati (FAP / FAP+Cat / SCR / Iniettori) — non funzionale, solo demo per cliente
const MockupApparatiHome = lazy(() => import('./pages/admin/mockup-apparati/MockupHome'))
const MockupApparatiAccettazione = lazy(() => import('./pages/admin/mockup-apparati/MockupAccettazione'))
const MockupApparatiFoglio = lazy(() => import('./pages/admin/mockup-apparati/MockupFoglioAccettazione'))
const MockupApparatiDaLavorare = lazy(() => import('./pages/admin/mockup-apparati/MockupDaLavorare'))
const MockupApparatiLavorazioneApparato = lazy(() => import('./pages/admin/mockup-apparati/MockupLavorazioneApparato'))
const MockupApparatiLavorazioneIniettore = lazy(() => import('./pages/admin/mockup-apparati/MockupLavorazioneIniettore'))
const MockupApparatiDaConsegnare = lazy(() => import('./pages/admin/mockup-apparati/MockupConsegna').then(m => ({ default: m.MockupDaConsegnare })))
const MockupApparatiConsegna = lazy(() => import('./pages/admin/mockup-apparati/MockupConsegna'))
const MockupApparatiBancaDati = lazy(() => import('./pages/admin/mockup-apparati/MockupBancaDati'))
const MockupApparatiAdminConfig = lazy(() => import('./pages/admin/mockup-apparati/MockupAdminConfig'))

// === Modulo Apparati di scarico (versione reale, post-mockup approvato) ===
const ApparatiAdminConfig = lazy(() => import('./pages/admin/apparati/AdminConfig'))
// ApparatiHome (hub /admin/apparati) deprecato: rotta ora redirect a /dashboard. Import rimosso.
// const ApparatiHome = lazy(() => import('./pages/admin/apparati/Home'))
const ApparatiAccettazione = lazy(() => import('./pages/admin/apparati/Accettazione'))
const ApparatiFoglioAccettazione = lazy(() => import('./pages/admin/apparati/FoglioAccettazione'))
const AccessoFred = lazy(() => import('./pages/AccessoFredPage'))
const DiagnosticaCamera = lazy(() => import('./pages/DiagnosticaCameraPage'))
const QrProva = lazy(() => import('./pages/QrProvaPage'))
const ApparatiFoglioQR = lazy(() => import('./pages/admin/apparati/FoglioQR'))
const ApparatiDaLavorare = lazy(() => import('./pages/admin/apparati/DaLavorare'))
const ApparatiLavorazioneApparato = lazy(() => import('./pages/admin/apparati/LavorazioneApparato'))
const ApparatiLavorazioneIniettore = lazy(() => import('./pages/admin/apparati/LavorazioneIniettore'))
const ApparatiBancaDati = lazy(() => import('./pages/admin/apparati/BancaDati'))
const ApparatiNotificheProgrammiTest = lazy(() => import('./pages/admin/apparati/NotificheProgrammiTest'))
const ApparatiGestioneSetupBancoProva = lazy(() => import('./pages/admin/apparati/GestioneSetupBancoProva'))

const OfficinaWelcomePage = lazy(() => import('./pages/mobile-officina/OfficinaWelcomePage'))
const OfficinaQRLoginPage = lazy(() => import('./pages/mobile-officina/OfficinaQRLoginPage'))
const OfficinaLoginDiretto = lazy(() => import('./pages/mobile-officina/OfficinaLoginDirettoPage'))
const OfficinaDashboardPage = lazy(() => import('./pages/mobile-officina/OfficinaDashboardPage'))
const OfficinaSearchPage = lazy(() => import('./pages/mobile-officina/OfficinaSearchPage'))
const OfficinaChangePINPage = lazy(() => import('./pages/mobile-officina/OfficinaChangePINPage'))
const OfficinaAnalisiPage = lazy(() => import('./pages/mobile-officina/OfficinaAnalisiPage'))
const OfficinaLavorazionePage = lazy(() => import('./pages/mobile-officina/OfficinaLavorazionePage'))
const OfficinaAnalisiApparatoPage = lazy(() => import('./pages/mobile-officina/OfficinaAnalisiApparatoPage'))
const OfficinaLavorazioneApparatoPage = lazy(() => import('./pages/mobile-officina/OfficinaLavorazioneApparatoPage'))
const OfficinaAnalisiIniettoriPage = lazy(() => import('./pages/mobile-officina/OfficinaAnalisiIniettoriPage'))
const OfficinaLavorazioneIniettoriPage = lazy(() => import('./pages/mobile-officina/OfficinaLavorazioneIniettoriPage'))
const MenuHub = lazy(() => import('./pages/apps/MenuHub'))

import './App.css'
import CustomForgotPassword from './components/auth/CustomForgotPassword'
import ResetPasswordPage from './components/auth/ResetPasswordPage'
import EmailChangeSuccessPage from './components/auth/EmailChangeSuccessPage'
import CompleteProfilePage from './pages/CompleteProfilePage'
import ProfileGuard from './components/auth/ProfileGuard'
// lazy: TestAdminPage  (was: import TestAdminPage from './pages/TestAdminPage')
// lazy: TestControlliPage  (was: import TestControlliPage from './pages/TestControlliPage')
// lazy: TestMockupPreventivoPage  (was: import TestMockupPreventivoPage from './pages/TestMockupPreventivoPage')
// lazy: TestProfessionalPage  (was: import TestProfessionalPage from './components/professional/TestPage')
// lazy: TestModalScrollPage  (was: import TestModalScrollPage from './pages/TestModalScrollPage')
// lazy: TestLandscapeWidthPage  (was: import TestLandscapeWidthPage from './pages/TestLandscapeWidthPage')
// lazy: UIDialogicomPage  (was: import UIDialogicomPage from './pages/UIDialogicomPage')
// lazy: UIDialogicomRel2Page  (was: import UIDialogicomRel2Page from './pages/UIDialogicomRel2Page')
// lazy: TestUniversalPhotoCropPage  (was: import TestUniversalPhotoCropPage from './pages/TestUniversalPhotoCrop')
// lazy: TestUniversalPhotoCropSimplePage  (was: import TestUniversalPhotoCropSimplePage from './pages/TestUniversalPhotoCropSimple')
// lazy: MobileCameraPage  (was: import MobileCameraPage from './pages/MobileCameraPage')
// lazy: MobileCameraPersistentPage  (was: import MobileCameraPersistentPage from './pages/MobileCameraPersistentPage')
// lazy: MobilePhotoRequestsPage  (was: import MobilePhotoRequestsPage from './pages/MobilePhotoRequestsPage')
// lazy: MobileRemotePhoto  (was: import MobileRemotePhoto from './pages/MobileRemotePhoto')
// lazy: PushTestPage  (was: import PushTestPage from './pages/PushTestPage')
// lazy: TestRemoteCameraPage  (was: import TestRemoteCameraPage from './pages/TestRemoteCameraPage')
// lazy: BusinessCardOCRPage  (was: import BusinessCardOCRPage from './pages/BusinessCardOCRPage')

// Nuove pagine Admin
import AdminLayout from './components/admin/AdminLayout'
// lazy: UserListPage  (was: import UserListPage from './pages/admin/UserListPage')
// lazy: GroupListPage  (was: import GroupListPage from './pages/admin/GroupListPage')
// lazy: UserProfileFormPage  (was: import UserProfileFormPage from './pages/admin/UserProfileFormPage')
// lazy: PendingCustomersPage  (was: import PendingCustomersPage from './pages/admin/PendingCustomersPage')
// lazy: PendingTurbinesPage  (was: import PendingTurbinesPage from './pages/admin/PendingTurbinesPage')
// lazy: PendingPage  (was: import PendingPage from './pages/admin/PendingPage')
// lazy: NuovoInserimentoDesktopPage  (was: import NuovoInserimentoDesktopPage from './pages/admin/NuovoInserimentoDesktopPage')
// lazy: DesktopBusinessCardPage  (was: import DesktopBusinessCardPage from './pages/admin/DesktopBusinessCardPage')
// lazy: AccettazioneRapidaPage  (was: import AccettazioneRapidaPage from './pages/admin/AccettazioneRapidaPage')
// lazy: InserimentoClienteRapidoPage  (was: import InserimentoClienteRapidoPage from './pages/admin/InserimentoClienteRapidoPage')
// lazy: OpenAIConfigPage  (was: import OpenAIConfigPage from './pages/admin/OpenAIConfigPage')
// lazy: AIKnowledgeBasePage  (was: import AIKnowledgeBasePage from './pages/admin/AIKnowledgeBasePage')
// lazy: ConfigurationHubPageStandalone  (was: import ConfigurationHubPageStandalone from './pages/admin/ConfigurationHubPageStandalone')
// lazy: MobileOperatorsManagementPage  (was: import MobileOperatorsManagementPage from './pages/admin/MobileOperatorsManagementPage')
// lazy: AccettazioneV2Page  (was: import AccettazioneV2Page from './pages/admin/AccettazioneV2Page')
// lazy: DesktopPhotoRequestsPage  (was: import DesktopPhotoRequestsPage from './pages/DesktopPhotoRequestsPage')
// lazy: DataCleanupPage  (was: import DataCleanupPage from './pages/admin/DataCleanupPage')
// lazy: ForceUpdatePage  (was: import ForceUpdatePage from './pages/admin/ForceUpdatePage')

// Mobile Operators
// lazy: MobileOperatorWelcomePage  (was: import MobileOperatorWelcomePage from './pages/MobileOperatorWelcomePage')
// lazy: MobileOperatorQRLoginPage  (was: import MobileOperatorQRLoginPage from './pages/MobileOperatorQRLoginPage')
// lazy: MobileOperatorDashboardPage  (was: import MobileOperatorDashboardPage from './pages/MobileOperatorDashboardPage')
// lazy: MobileOperatorChangePINPage  (was: import MobileOperatorChangePINPage from './pages/MobileOperatorChangePINPage')

// Mobile Operator Workflow Pages
// lazy: AccettazionePageMobile  (was: import AccettazionePageMobile from './pages/mobile-operator/AccettazionePage')
// lazy: AnalisiPageMobile  (was: import AnalisiPageMobile from './pages/mobile-operator/AnalisiPage')
// lazy: RiparazionePageMobile  (was: import RiparazionePageMobile from './pages/mobile-operator/RiparazionePage')
// lazy: ConsegnaPageMobile  (was: import ConsegnaPageMobile from './pages/mobile-operator/ConsegnaPage')
// lazy: UtilityPageMobile  (was: import UtilityPageMobile from './pages/mobile-operator/UtilityPage')

// Mobile Operator Legacy Pages
// lazy: PhotoCapturePage  (was: import PhotoCapturePage from './pages/mobile-operator/PhotoCapturePage')
// lazy: WorkOrdersPage  (was: import WorkOrdersPage from './pages/mobile-operator/WorkOrdersPage')
// lazy: CheckInsPage  (was: import CheckInsPage from './pages/mobile-operator/CheckInsPage')
// lazy: InventoryPage  (was: import InventoryPage from './pages/mobile-operator/InventoryPage')

// Mobile Officina
// lazy: OfficinaWelcomePage  (was: import OfficinaWelcomePage from './pages/mobile-officina/OfficinaWelcomePage')
// lazy: OfficinaQRLoginPage  (was: import OfficinaQRLoginPage from './pages/mobile-officina/OfficinaQRLoginPage')
// lazy: OfficinaDashboardPage  (was: import OfficinaDashboardPage from './pages/mobile-officina/OfficinaDashboardPage')
// lazy: OfficinaSearchPage  (was: import OfficinaSearchPage from './pages/mobile-officina/OfficinaSearchPage')
// lazy: OfficinaChangePINPage  (was: import OfficinaChangePINPage from './pages/mobile-officina/OfficinaChangePINPage')
// lazy: OfficinaAnalisiPage  (was: import OfficinaAnalisiPage from './pages/mobile-officina/OfficinaAnalisiPage')
// lazy: OfficinaLavorazionePage  (was: import OfficinaLavorazionePage from './pages/mobile-officina/OfficinaLavorazionePage')

// import TestCoreComponents from './test-core-imports'

// Phase 3: Apps Modulari
// lazy: MenuHub  (was: import MenuHub from './pages/apps/MenuHub')
import LavorazioneApp from './apps/lavorazione/LavorazioneApp'

export default function App() {
  console.log('%c--- CARICATA APPLICAZIONE v2 - BUILD: ' + Date.now() + ' ---', 'color: #00ff00; font-size: 16px; font-weight: bold;');

  // ✅ NUOVO: Redirect automatico iOS/Android alla sezione mobile operator
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isRoot = window.location.pathname === '/';
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true;

    if (isMobile && isRoot && isStandalone) {
      console.log('📱 iOS/Android PWA rilevata sulla root → Redirect a /mobile-operator/');
      window.location.replace('/mobile-operator/');
    }
  }, []);

  // Disabilita Google One Tap automatico globalmente
  useEffect(() => {
    const disableGoogleOneTap = () => {
      try {
        const g = (window as any).google;
        if (g?.accounts?.id) {
          // Cancella qualsiasi prompt One Tap attivo
          g.accounts.id.cancel();
          console.log('✅ Google One Tap disabled globally');
        }
      } catch (e) {
        // Ignora errori se Google GIS non è ancora caricato
      }
    };

    // Esegui immediatamente
    disableGoogleOneTap();

    // E anche dopo un breve delay per catturare inizializzazioni tardive
    const timer = setTimeout(disableGoogleOneTap, 500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <TenantProvider>
      <BrowserRouter>
        <AuthProvider>
          <SedeProvider>
          <PendingCountsProvider>
          <ToastProvider>
            <AppDialogHost />
            <PieroZoomer />
            <OrphanPhotoWatcher />
            <BancaDatiNotifier />
            <DevSimulatorProvider>
            <GlobalModalsProvider>
            <OperatoreAttivoProvider>
            <RemoteScanProvider>
            <MobileScanReceiver />
            <Suspense fallback={<div style={{padding:24,textAlign:'center'}}>Caricamento…</div>}><Routes>
            {/* Route test isolate */}
            <Route path="/test-admin" element={<TestAdminPage />} />
            <Route path="/test-controlli" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <AppLayout>
                  <TestControlliPage />
                </AppLayout>
              </RequireAuth>
            } />
            <Route path="/test-professional" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <AppLayout>
                  <TestProfessionalPage />
                </AppLayout>
              </RequireAuth>
            } />
            <Route path="/test-mockup-preventivo" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <AppLayout>
                  <TestMockupPreventivoPage />
                </AppLayout>
              </RequireAuth>
            } />
            <Route path="/ui-dialogicom" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <UIDialogicomPage />
              </RequireAuth>
            } />
            <Route path="/ui-dialogicom-rel_2" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <UIDialogicomRel2Page />
              </RequireAuth>
            } />
            <Route path="/test-universal-photo-crop" element={
              <RequireAuth allowedRoles={['superadmin', 'admin']}>
                <TestUniversalPhotoCropSimplePage />
              </RequireAuth>
            } />
            <Route path="/test-modal-scroll" element={
              <AppLayout>
                <TestModalScrollPage />
              </AppLayout>
            } />
            <Route path="/test-landscape-width" element={<TestLandscapeWidthPage />} />
            <Route path="/test-remote-camera" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <TestRemoteCameraPage />
              </RequireAuth>
            } />
            <Route path="/business-card-ocr" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <BusinessCardOCRPage />
              </RequireAuth>
            } />
            {/* TODO: Riabilitare dopo fix import PhotoCapture */}
            {/* <Route path="/test-core" element={
              <RequireAuth allowedRoles={['superadmin']}>
                <AppLayout>
                  <TestCoreComponents />
                </AppLayout>
              </RequireAuth>
            } /> */}

            {/* Route pubblica per fotocamera mobile (QR code scan) */}
            <Route path="/mobile-camera" element={<MobileCameraPage />} />

            {/* Route pubblica per fotocamera mobile persistente (sessione continua) */}
            <Route path="/mobile-camera-persistent" element={<MobileCameraPersistentPage />} />

            {/* Route desktop per inviare richieste foto via WebSocket */}
            <Route path="/desktop-photo-requests" element={<DesktopPhotoRequestsPage />} />

            {/* Route pubblica per Mobile Photo Requests (nuovo sistema real-time) */}
            <Route path="/mobile-photo-requests" element={<MobilePhotoRequestsPage />} />

            {/* Route admin per gestione operatori mobile */}
            <Route path="/admin/mobile-operators" element={
              <RequireAuth allowedRoles={['admin', 'superadmin']}>
                <MobileOperatorsManagementPage />
              </RequireAuth>
            } />

            {/* Route pubblica per Remote Photo (sistema unificato foto mobile) */}
            <Route path="/remote-photo" element={<MobileRemotePhoto />} />

            {/* Route pubblica per Remote Scan (lettore barcode/QR remoto) */}
            <Route path="/remote-scan" element={<MobileRemoteScan />} />

            {/* Route pubblica per test push notifications (iOS/Safari testing) */}
            <Route path="/push-test" element={<PushTestPage />} />

            {/* === MOBILE OPERATORS AUTHENTICATION === */}
            {/* Welcome page - punto di ingresso webapp mobile */}
            <Route path="/mobile-operator" element={<MobileOperatorWelcomePage />} />

            {/* Login via QR temporaneo da PC autorizzato (UNICO metodo) */}
            <Route path="/mobile-operator/qr-login" element={<MobileOperatorQRLoginPage />} />

            {/* Dashboard operatore (protetta da hook auth) */}
            <Route path="/mobile-operator/dashboard" element={<MobileOperatorDashboardPage />} />

            {/* Cambio PIN (protetto da hook auth) */}
            <Route path="/mobile-operator/change-pin" element={<MobileOperatorChangePINPage />} />

            {/* CoronaTek Preventivi — webapp mobile approvatori (login standard) */}
            <Route path="/mobile-preventivi" element={
              <RequireAuth allowedRoles={['superadmin', 'admin']} allowedGroups={['accettazione', 'magazzino', 'officina', 'contabilita']}>
                <MobilePreventiviLayout />
              </RequireAuth>
            }>
              <Route index element={<MobilePreventiviCoda />} />
              <Route path="revisione/:analisiId" element={<MobilePreventiviRevisione />} />
              <Route path="preventivo/:turbinaId" element={<MobilePreventiviPreventivo />} />
              <Route path="valorizzazioni" element={<MobilePreventiviValorizzazioni />} />
              <Route path="stampe" element={<MobilePreventiviStampe />} />
              {/* Accettazioni in stato da_approvare: correggi i dati e approva.
                  Distinta dalla tab "Da approvare" della coda, che riguarda le
                  ANALISI: qui si approva la scheda di accettazione. */}
              <Route path="da-approvare" element={<MobilePreventiviDaApprovare />} />
              {/* Clienti temporanei da confermare: senza questo bastava un
                  cliente nuovo a fermare la catena fino al rientro in ufficio. */}
              <Route path="clienti-da-approvare" element={<MobilePreventiviClienti />} />
              {/* Fine lavoro di un apparato: si RIVEDE e poi si approva.
                  Approvare da una riga di elenco era un timbro, non una decisione. */}
              <Route path="apparato/:apparatoId" element={<MobilePreventiviApparato />} />
            </Route>

            {/* Mobile Operator - Workflow Pages */}
            <Route path="/mobile-operator/workflow/accettazione" element={<AccettazionePageMobile />} />
            <Route path="/mobile-operator/workflow/analisi" element={<AnalisiPageMobile />} />
            <Route path="/mobile-operator/workflow/riparazione" element={<RiparazionePageMobile />} />
            <Route path="/mobile-operator/workflow/consegna" element={<ConsegnaPageMobile />} />
            <Route path="/mobile-operator/workflow/utility" element={<UtilityPageMobile />} />

            {/* Mobile Operator - Test Photo Crop */}
            <Route path="/mobile-operator/test-universal-photo-crop" element={<TestUniversalPhotoCropPage />} />

            {/* Mobile Operator - Nuovo Cliente */}
            <Route path="/mobile-operator/digitalizza-biglietto" element={<DigitalizzaBigliettoPage />} />
            <Route path="/mobile-operator/compila-dati-essenziali" element={<CompilaDatiEssenzialiPage />} />

            {/* Mobile Operator - Legacy Pages */}
            <Route path="/mobile-operator/photo-capture/:entityId" element={<PhotoCapturePage />} />
            <Route path="/mobile-operator/work-orders" element={<WorkOrdersPage />} />
            <Route path="/mobile-operator/check-ins" element={<CheckInsPage />} />
            <Route path="/mobile-operator/inventory" element={<InventoryPage />} />

            {/* === MOCKUP MODULO APPARATI (demo per cliente, no backend) === */}
            <Route path="/admin/mockup-apparati" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiHome />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/accettazione/:tipo" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiAccettazione />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/foglio/:codice" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiFoglio />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/da-lavorare" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiDaLavorare />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/lavorazione/apparato/:apparatoId" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiLavorazioneApparato />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/lavorazione/iniettore/:apparatoId" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiLavorazioneIniettore />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/da-consegnare" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiDaConsegnare />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/consegna/:apparatoId" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiConsegna />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/banca-dati" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiBancaDati />
              </RequireAuth>
            } />
            <Route path="/admin/mockup-apparati/admin-config" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <MockupApparatiAdminConfig />
              </RequireAuth>
            } />

            {/* === Modulo Apparati di scarico — VERSIONE REALE ===
                L'hub /admin/apparati risale ai primi test e non serve più: le sue funzioni sono ora
                nel menù Amministrazione (banca dati, richieste, setup, config prezzi, notifiche) e in
                dashboard (accettazione FAP/SCR/iniettori). Redirect a /dashboard → hub irraggiungibile.
                Le sotto-rotte /admin/apparati/* restano attive. */}
            <Route path="/admin/apparati" element={<Navigate to="/dashboard" replace />} />
            <Route path="/admin/apparati/accettazione/:tipo" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiAccettazione />
              </RequireAuth>
            } />
            {/* Diagnostica webcam: senza login di proposito — serve proprio a chi non
                riesce a usare il PC, e non mostra dati aziendali. */}
            <Route path="/diagnostica-camera" element={<DiagnosticaCamera />} />
            {/* Finestra separata con il solo QR di prova, pilotata dalla diagnostica. */}
            <Route path="/qr-prova" element={<QrProva />} />
            {/* Pagina sola per far entrare un operatore in Fred (QR + apertura sullo stesso PC).
                Dietro login: genera token d'accesso, non deve bastare conoscere l'URL. */}
            <Route path="/accesso-fred" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <AccessoFred />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/foglio/:id" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiFoglioAccettazione />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/foglio-qr/:id" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiFoglioQR />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/da-lavorare" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiDaLavorare />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/lavorazione/apparato/:id" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiLavorazioneApparato />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/lavorazione/iniettore/:id" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiLavorazioneIniettore />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/banca-dati" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiBancaDati />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/programmi-test/notifiche" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiNotificheProgrammiTest />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/setup-banco-prova" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiGestioneSetupBancoProva />
              </RequireAuth>
            } />
            <Route path="/admin/apparati/admin-config" element={
              <RequireAuth allowedRoles={['admin', 'superadmin', 'user']}>
                <ApparatiAdminConfig />
              </RequireAuth>
            } />

            {/* === MOBILE OFFICINA === */}
            <Route path="/mobile-officina" element={<OfficinaWelcomePage />} />
            <Route path="/mobile-officina/qr-login" element={<OfficinaQRLoginPage />} />
            {/* Accesso diretto nome utente + PIN, per le postazioni fisse d'officina. */}
            <Route path="/mobile-officina/login" element={<OfficinaLoginDiretto />} />
            <Route path="/mobile-officina/search" element={<OfficinaSearchPage />} />
            <Route path="/mobile-officina/dashboard" element={<OfficinaDashboardPage />} />
            <Route path="/mobile-officina/change-pin" element={<OfficinaChangePINPage />} />
            <Route path="/mobile-officina/analisi/:turbineId" element={<OfficinaAnalisiPage />} />
            <Route path="/mobile-officina/lavorazione/:turbineId" element={<OfficinaLavorazionePage />} />
            <Route path="/mobile-officina/analisi-apparato/:id" element={<OfficinaAnalisiApparatoPage />} />
            <Route path="/mobile-officina/lavorazione-apparato/:id" element={<OfficinaLavorazioneApparatoPage />} />
            <Route path="/mobile-officina/analisi-iniettori/:id" element={<OfficinaAnalisiIniettoriPage />} />
            <Route path="/mobile-officina/lavorazione-iniettori/:id" element={<OfficinaLavorazioneIniettoriPage />} />

            {/* === PHASE 3: APPS MODULARI === */}
            {/* Menu Hub - Launcher Apps */}
            <Route path="/menu-hub" element={
              <RequireAuth allowedRoles={['superadmin', 'admin', 'user']}>
                <MenuHub />
              </RequireAuth>
            } />

            {/* App Lavorazione */}
            <Route path="/apps/lavorazione/*" element={
              <RequireAuth allowedRoles={['superadmin', 'admin', 'user']}>
                <LavorazioneApp />
              </RequireAuth>
            } />

            {/* Pagina pagamento pubblica (no auth) */}
            <Route path="/pagamento/:token" element={<PagamentoPage />} />

            {/* Route pubbliche */}
            <Route path="/login" element={<CustomSignIn />} />
            <Route path="/sign-in" element={<CustomSignIn />} />
            <Route path="/direct-login" element={<DirectLogin />} />
            <Route path="/sign-up" element={<CustomSignUp />} />
            <Route path="/register" element={<CustomSignUp />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/forgot-password" element={<CustomForgotPassword />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/email-change-success" element={<EmailChangeSuccessPage />} />

            {/* Rotta per completamento profilo obbligatorio */}
            <Route 
              path="/complete-profile" 
              element={
                <RequireAuth>
                  <CompleteProfilePage />
                </RequireAuth>
              } 
            />
            
            {/* Route protette */}
            <Route path="/dashboard" element={
              <RequireAuth>
                <ProfileGuard>
                  <AccettazioneV2Page />
                </ProfileGuard>
              </RequireAuth>
            } />
            <Route path="/officina" element={
              <RequireAuth>
                <AppLayout>
                  <OfficinaPage />
                </AppLayout>
              </RequireAuth>
            } />
            {/* AdminPageLayout al posto di AppLayout: quest'ultimo monta la
                sidebar del profilo, non il menu di navigazione con i badge. */}
            <Route path="/admin/audit-operatori" element={
              <RequireAuth>
                <AdminPageLayout>
                  <AuditOperatoriPage />
                </AdminPageLayout>
              </RequireAuth>
            } />
            <Route path="/magazzino" element={
              <RequireAuth>
                <MagazzinoV2Page />
              </RequireAuth>
            } />
            <Route path="/magazzino-old" element={
              <RequireAuth>
                <MagazzinoPage />
              </RequireAuth>
            } />
            <Route path="/pagamenti" element={
              <RequireAuth>
                <PagamentiPage />
              </RequireAuth>
            } />
            <Route path="/accettazione" element={
              <RequireAuth>
                <AccettazionePage />
              </RequireAuth>
            } />
            <Route path="/accettazione/digitalizza-biglietto" element={
              <RequireAuth>
                <DigitalizzaBigliettoPage />
              </RequireAuth>
            } />
            <Route path="/accettazione/compila-dati-essenziali" element={
              <RequireAuth>
                <CompilaDatiEssenzialiPage />
              </RequireAuth>
            } />
            <Route path="/test-accettazione-menu" element={
              <RequireAuth>
                <TestAccettazioneMenuPage />
              </RequireAuth>
            } />
            <Route path="/analisi-guasto-preventivi" element={
              <RequireAuth allowedRoles={['superadmin', 'admin']} allowedGroups={['officina']}>
                <AppLayout>
                  <AnalisiGuastoPage />
                </AppLayout>
              </RequireAuth>
            } />
            <Route path="/analisi-guasti-preventivi" element={
              <RequireAuth allowedRoles={['superadmin', 'admin']} allowedGroups={['accettazione', 'magazzino', 'officina', 'contabilita']}>
                <AnalisiGuastiPage />
              </RequireAuth>
            } />
            <Route path="/analisi-guasti-preventivi-old" element={
              <RequireAuth allowedRoles={['superadmin', 'admin']} allowedGroups={['accettazione', 'magazzino', 'officina', 'contabilita']}>
                <AnalisiGuastiPageOld />
              </RequireAuth>
            } />
            <Route path="/ufficio" element={
              <RequireAuth>
                <AppLayout>
                  <UfficioPage />
                </AppLayout>
              </RequireAuth>
            } />
            <Route path="/clienti" element={
              <RequireAuth>
                <AppLayout>
                  <ClientiPage />
                </AppLayout>
              </RequireAuth>
            } />
            
            {/* Sezione Amministrazione Protetta */}
            <Route
              path="/admin"
              element={
                <RequireAuth allowedRoles={['superadmin', 'admin']} allowedGroups={['accettazione']}>
                  <AdminLayout />
                </RequireAuth>
              }
            >
              <Route index element={<Navigate to="users" replace />} />
              <Route path="users" element={<UserListPage />} />
              <Route 
                path="groups" 
                element={
                  <RequireAuth allowedRoles={['superadmin']}>
                    <GroupListPage />
                  </RequireAuth>
                } 
              />
              <Route path="profile-form" element={<UserProfileFormPage />} />
              <Route
                path="pending-customers"
                element={
                  <RequireAuth allowedRoles={['admin', 'superadmin']}>
                    <PendingCustomersPage />
                  </RequireAuth>
                }
              />
              <Route
                path="pending"
                element={
                  <RequireAuth allowedRoles={['admin', 'superadmin']}>
                    <PendingPage />
                  </RequireAuth>
                }
              />
              <Route
                path="pending-turbines"
                element={
                  <RequireAuth allowedRoles={['admin', 'superadmin']}>
                    <PendingTurbinesPage />
                  </RequireAuth>
                }
              />
              <Route
                path="nuovo-inserimento"
                element={
                  <RequireAuth allowedRoles={['admin', 'superadmin']}>
                    <NuovoInserimentoDesktopPage />
                  </RequireAuth>
                }
              />
              <Route
                path="new-customer/business-card"
                element={
                  <RequireAuth allowedRoles={['admin', 'superadmin']}>
                    <DesktopBusinessCardPage />
                  </RequireAuth>
                }
              />
            </Route>

            {/* Route indipendente per Accettazione Rapida (senza tab Utenti/Gruppi) */}
            <Route
              path="/admin/accettazione-rapida"
              element={
                <RequireAuth allowedRoles={['admin', 'superadmin']}>
                  <AccettazioneRapidaPage />
                </RequireAuth>
              }
            />

            {/* Route per Inserimento Rapido Cliente */}
            <Route
              path="/admin/inserimento-cliente-rapido"
              element={
                <RequireAuth allowedRoles={['admin', 'superadmin']}>
                  <InserimentoClienteRapidoPage />
                </RequireAuth>
              }
            />

            {/* Route indipendente per Configurazione AI */}
            <Route
              path="/admin/openai-config"
              element={
                <RequireAuth allowedRoles={['superadmin']}>
                  <AppLayout>
                    <OpenAIConfigPage />
                  </AppLayout>
                </RequireAuth>
              }
            />
            {/* Route per AI Knowledge Base */}
            <Route
              path="/admin/ai-knowledge"
              element={
                <RequireAuth allowedRoles={['superadmin']}>
                  <AppLayout>
                    <AIKnowledgeBasePage />
                  </AppLayout>
                </RequireAuth>
              }
            />
            {/* Route per Configuration Hub */}
            <Route
              path="/admin/config-hub"
              element={
                <RequireAuth allowedRoles={['superadmin']}>
                  <ConfigurationHubPageStandalone />
                </RequireAuth>
              }
            />

            {/* Route per Elimina Dati - Solo superadmin */}
            <Route
              path="/admin/data-cleanup"
              element={
                <RequireAuth allowedRoles={['superadmin']}>
                  <DataCleanupPage />
                </RequireAuth>
              }
            />
            <Route
              path="/admin/magazzino-sync"
              element={
                // Anche 'admin': l'accesso reale lo decide il backend in base al
                // flag magazzino_sync_visibile_admin, e la pagina nasconde da se'
                // i comandi distruttivi a chi non e' superadmin. Lasciare qui il
                // solo superadmin bloccava l'admin prima ancora del caricamento,
                // rendendo inutile tutto il resto del meccanismo.
                <RequireAuth allowedRoles={['admin', 'superadmin']}>
                  <MagazzinoSyncPage />
                </RequireAuth>
              }
            />

            {/* Route per Aggiornamento Forzato - Solo superadmin */}
            <Route
              path="/admin/force-update"
              element={
                <RequireAuth allowedRoles={['superadmin']}>
                  <ForceUpdatePage />
                </RequireAuth>
              }
            />

            {/* Route per Accettazione V2 - Dashboard Wizard */}
            <Route
              path="/admin/accettazione-v2"
              element={
                <RequireAuth allowedRoles={['admin', 'superadmin']}>
                  <AccettazioneV2Page />
                </RequireAuth>
              }
            />

            <Route path="/profile" element={
              <RequireAuth>
                <AppLayout>
                  <ProfileRoutePage />
                </AppLayout>
              </RequireAuth>
            } />
            
            {/* Redirect di default */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes></Suspense>
            </RemoteScanProvider>
            </OperatoreAttivoProvider>
            {/* DevSimulator - Pulsante flottante e pannello opzioni (globale) */}
            <DevSimulatorButton />
            <DevSimulatorPanel />
            <DevSimulatorConfirmModal />
            {/* Chat Widget globale - nascosto in modalità embedded (iframe). Rileva auth desktop o mobile (officina/operator) */}
            {!window.location.search.includes('embedded=true')
              && !window.location.pathname.startsWith('/mobile-officina')
              && !window.location.pathname.startsWith('/mobile-operator')
              && !window.location.pathname.startsWith('/mobile-preventivi')
              && <DesktopChatWidget />}
            {/* Pulsante sticky Login Operatori — sempre visibile su desktop */}
            {!window.location.search.includes('embedded=true')
              && !window.location.pathname.startsWith('/mobile-officina')
              && !window.location.pathname.startsWith('/mobile-operator')
              && !window.location.pathname.startsWith('/mobile-preventivi')
              && !window.location.pathname.startsWith('/login')
              && <StickyOperatorLoginButton />}
            </GlobalModalsProvider>
            </DevSimulatorProvider>
          </ToastProvider>
          </PendingCountsProvider>
          </SedeProvider>
        </AuthProvider>
      </BrowserRouter>
    </TenantProvider>
  )
}
